import { useEffect, useMemo, useRef, useState } from "react";

import {
  hasActiveBusinessTypeLabelConflict,
  normalizeBusinessTypeLabel,
  splitBusinessTypesByStatus,
} from "@/pages/businessTypes/businessTypeHelpers";
import {
  useArchiveBusinessTypeMutation,
  useCreateBusinessTypeMutation,
  useGetBusinessTypesQuery,
  useUnarchiveBusinessTypeMutation,
  useUpdateBusinessTypeMutation,
} from "@/redux/businessTypes";
import {
  getErrorMessage,
  getPublicErrorDetails,
  toError,
} from "@/utils/errorHandling";
import FrontendLoggerFactory from "@/utils/logging/FrontendLoggerFactory";
import {
  VNextButton,
  VNextDialog,
  VNextKeyValueList,
  VNextNotice,
  VNextStat,
} from "@/vnext/primitives";
import { BusinessTypesCreateCard } from "@/vnext/routes/settings/business-types/BusinessTypesCreateCard";
import { BusinessTypesListCard } from "@/vnext/routes/settings/business-types/BusinessTypesListCard";
import { SettingsWorkspaceChrome } from "@/vnext/routes/settings/SettingsWorkspaceChrome";

import "@/vnext/routes/settings/business-types/businessTypesMake.css";

const logger =
  FrontendLoggerFactory.getLogger().withComponent("BusinessTypesRoute");

interface PendingBusinessTypeLifecycleAction {
  kind: "archive" | "restore";
  id: string;
  label: string;
}

export default function BusinessTypesRoute() {
  const pageStartedAtRef = useRef<number | null>(null);
  const readyLoggedRef = useRef(false);
  const lifecycleActionInFlightRef = useRef(false);
  const {
    data: businessTypes,
    error,
    isFetching,
    isLoading,
    refetch,
  } = useGetBusinessTypesQuery();
  const [createBusinessType, { isLoading: isCreating }] =
    useCreateBusinessTypeMutation();
  const [updateBusinessType, { isLoading: isUpdating }] =
    useUpdateBusinessTypeMutation();
  const [archiveBusinessType, { isLoading: isArchiving }] =
    useArchiveBusinessTypeMutation();
  const [unarchiveBusinessType, { isLoading: isUnarchiving }] =
    useUnarchiveBusinessTypeMutation();

  const [newLabel, setNewLabel] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [pendingLifecycleAction, setPendingLifecycleAction] =
    useState<PendingBusinessTypeLifecycleAction | null>(null);
  const [isConfirmingLifecycleAction, setIsConfirmingLifecycleAction] =
    useState(false);

  const { activeTypes, inactiveTypes } = useMemo(
    () => splitBusinessTypesByStatus(businessTypes),
    [businessTypes],
  );

  const isBusy =
    isCreating ||
    isUpdating ||
    isArchiving ||
    isUnarchiving ||
    isConfirmingLifecycleAction ||
    isLoading ||
    isFetching;

  useEffect(() => {
    logger.debug("BUSINESS_TYPES_ROUTE_ENTER", {
      component: "BusinessTypesRoute",
      feature: "ADMIN",
      operation: "BUSINESS_TYPES_ROUTE_ENTER",
    });
    pageStartedAtRef.current = Date.now();
    readyLoggedRef.current = false;
  }, []);

  useEffect(() => {
    if (readyLoggedRef.current || isLoading || isFetching || error) {
      return;
    }

    readyLoggedRef.current = true;
    logger.debug("BUSINESS_TYPES_ROUTE_READY", {
      activeCount: activeTypes.length,
      archivedCount: inactiveTypes.length,
      component: "BusinessTypesRoute",
      feature: "ADMIN",
      operation: "BUSINESS_TYPES_ROUTE_READY",
      pageLoadDurationMs:
        pageStartedAtRef.current === null
          ? null
          : Date.now() - pageStartedAtRef.current,
      totalCount: activeTypes.length + inactiveTypes.length,
    });
  }, [activeTypes.length, error, inactiveTypes.length, isFetching, isLoading]);

  useEffect(() => {
    if (!error) {
      return;
    }

    const details = getPublicErrorDetails(
      error,
      "Failed to load business types.",
    );

    logger.error(
      "BUSINESS_TYPES_ROUTE_FAIL",
      toError(error) ?? new Error("business_types_route_failed"),
      {
        component: "BusinessTypesRoute",
        correlationId: details.correlationId ?? undefined,
        errorCode: details.errorCode,
        feature: "ADMIN",
        operation: "BUSINESS_TYPES_ROUTE_FAIL",
      },
    );
  }, [error]);

  const resetMessages = () => {
    setActionError(null);
    setActionSuccess(null);
  };

  const handleMutationFailure = (
    operation: string,
    fallbackMessage: string,
    mutationError: unknown,
  ) => {
    const details = getPublicErrorDetails(mutationError, fallbackMessage);
    setActionError(getErrorMessage(mutationError, fallbackMessage));
    logger.error(
      operation,
      toError(mutationError) ?? new Error("business_types_action_failed"),
      {
        component: "BusinessTypesRoute",
        correlationId: details.correlationId ?? undefined,
        errorCode: details.errorCode,
        feature: "ADMIN",
        operation,
      },
    );
  };

  const handleCreate = async () => {
    resetMessages();
    const label = normalizeBusinessTypeLabel(newLabel);
    if (!label) {
      setActionError("Enter a business type label.");
      logger.warn("BUSINESS_TYPES_CREATE_VALIDATION_FAIL", {
        component: "BusinessTypesRoute",
        feature: "ADMIN",
        operation: "BUSINESS_TYPES_CREATE_VALIDATION_FAIL",
      });
      return;
    }

    try {
      await createBusinessType({ label }).unwrap();
      setActionSuccess(`Added "${label}"`);
      setNewLabel("");
    } catch (mutationError) {
      handleMutationFailure(
        "BUSINESS_TYPES_CREATE_FAIL",
        "Failed to add business type.",
        mutationError,
      );
    }
  };

  const handleStartEdit = (id: string, label: string) => {
    resetMessages();
    setEditingId(id);
    setEditingLabel(label);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingLabel("");
  };

  const handleSaveEdit = async (id: string) => {
    resetMessages();
    const label = normalizeBusinessTypeLabel(editingLabel);

    if (!label) {
      setActionError("Label cannot be empty.");
      logger.warn("BUSINESS_TYPES_UPDATE_VALIDATION_FAIL", {
        component: "BusinessTypesRoute",
        feature: "ADMIN",
        operation: "BUSINESS_TYPES_UPDATE_VALIDATION_FAIL",
      });
      return;
    }

    try {
      await updateBusinessType({
        id,
        payload: { label },
      }).unwrap();
      setActionSuccess("Business type updated.");
      handleCancelEdit();
    } catch (mutationError) {
      handleMutationFailure(
        "BUSINESS_TYPES_UPDATE_FAIL",
        "Failed to update business type.",
        mutationError,
      );
    }
  };

  const openArchiveConfirmation = (id: string, label: string) => {
    resetMessages();
    setPendingLifecycleAction({
      kind: "archive",
      id,
      label,
    });
  };

  const openRestoreConfirmation = (id: string, label: string) => {
    resetMessages();
    setPendingLifecycleAction({
      kind: "restore",
      id,
      label,
    });
  };

  const closeLifecycleConfirmation = () => {
    if (lifecycleActionInFlightRef.current) {
      return;
    }

    setPendingLifecycleAction(null);
  };

  const handleConfirmLifecycleAction = async (): Promise<void> => {
    const pendingAction = pendingLifecycleAction;
    if (!pendingAction || lifecycleActionInFlightRef.current) {
      return;
    }

    lifecycleActionInFlightRef.current = true;
    setIsConfirmingLifecycleAction(true);
    resetMessages();

    try {
      if (pendingAction.kind === "archive") {
        await archiveBusinessType(pendingAction.id).unwrap();
        setActionSuccess(`Archived "${pendingAction.label}"`);
      } else {
        await unarchiveBusinessType(pendingAction.id).unwrap();
        setActionSuccess(`Restored "${pendingAction.label}"`);
      }
      setPendingLifecycleAction(null);
    } catch (mutationError) {
      handleMutationFailure(
        pendingAction.kind === "archive"
          ? "BUSINESS_TYPES_ARCHIVE_FAIL"
          : "BUSINESS_TYPES_UNARCHIVE_FAIL",
        pendingAction.kind === "archive"
          ? "Failed to archive business type."
          : "Failed to restore business type.",
        mutationError,
      );
      setPendingLifecycleAction(null);
    } finally {
      lifecycleActionInFlightRef.current = false;
      setIsConfirmingLifecycleAction(false);
    }
  };

  return (
    <SettingsWorkspaceChrome
      actions={
        <div className="business-types-route__toolbar">
          <VNextButton
            disabled={isFetching}
            onClick={() => void refetch()}
            tone="secondary"
          >
            {isFetching ? "Refreshing…" : "Refresh"}
          </VNextButton>
        </div>
      }
      subtitle="Manage the business type choices shown during onboarding in admin and customer setup."
      title="Business types"
    >
      <div className="business-types-route">
        <VNextNotice tone="info">
          Use this Settings surface to keep onboarding business types current
          without reopening the old admin tooling.
        </VNextNotice>

        {actionSuccess ? (
          <VNextNotice
            action={
              <VNextButton onClick={() => setActionSuccess(null)} tone="quiet">
                Dismiss
              </VNextButton>
            }
            tone="info"
          >
            {actionSuccess}
          </VNextNotice>
        ) : null}

        {actionError ? (
          <VNextNotice
            action={
              <VNextButton onClick={() => setActionError(null)} tone="quiet">
                Dismiss
              </VNextButton>
            }
            tone="danger"
          >
            {actionError}
          </VNextNotice>
        ) : null}

        {error ? (
          <VNextNotice
            action={
              <VNextButton onClick={() => void refetch()} tone="quiet">
                Retry
              </VNextButton>
            }
            tone="danger"
          >
            Failed to load business types.
          </VNextNotice>
        ) : null}

        <div className="business-types-route__stats">
          <VNextStat
            detail="Active business types available today"
            label="Active"
            value={activeTypes.length}
          />
          <VNextStat
            detail="Archived choices kept for audit and restore"
            label="Archived"
            value={inactiveTypes.length}
          />
          <VNextStat
            detail="Total configured business types"
            label="Total"
            value={activeTypes.length + inactiveTypes.length}
          />
        </div>

        <BusinessTypesCreateCard
          isBusy={isBusy}
          newLabel={newLabel}
          onCreate={() => void handleCreate()}
          onLabelChange={setNewLabel}
        />

        <div className="business-types-route__lists">
          <BusinessTypesListCard
            editingId={editingId}
            editingLabel={editingLabel}
            emptyState={
              isLoading
                ? "Loading business types…"
                : "No active business types."
            }
            isBusy={isBusy}
            items={activeTypes}
            onAction={openArchiveConfirmation}
            onCancelEdit={handleCancelEdit}
            onEditingLabelChange={setEditingLabel}
            onSaveEdit={(id) => {
              void handleSaveEdit(id);
            }}
            onStartEdit={handleStartEdit}
            title="Active business types"
            tone="warning"
          />
          <BusinessTypesListCard
            getActionDisabledReason={(item) =>
              hasActiveBusinessTypeLabelConflict(item.label, activeTypes)
                ? "Restore unavailable: An active business type already uses this label."
                : undefined
            }
            editingId={editingId}
            editingLabel={editingLabel}
            emptyState={
              isLoading
                ? "Loading business types…"
                : "No archived business types."
            }
            isBusy={isBusy}
            items={inactiveTypes}
            onAction={openRestoreConfirmation}
            onCancelEdit={handleCancelEdit}
            onEditingLabelChange={setEditingLabel}
            onSaveEdit={(id) => {
              void handleSaveEdit(id);
            }}
            title="Archived business types"
            tone="default"
          />
        </div>

        {pendingLifecycleAction ? (
          <VNextDialog
            description={
              pendingLifecycleAction.kind === "archive"
                ? "Review before removing this option from onboarding."
                : "Review before adding this option back to onboarding."
            }
            footer={
              <>
                <VNextButton
                  disabled={isConfirmingLifecycleAction}
                  onClick={closeLifecycleConfirmation}
                  tone="secondary"
                >
                  Cancel
                </VNextButton>
                <VNextButton
                  disabled={isConfirmingLifecycleAction}
                  onClick={() => {
                    void handleConfirmLifecycleAction();
                  }}
                  tone="primary"
                >
                  {isConfirmingLifecycleAction
                    ? pendingLifecycleAction.kind === "archive"
                      ? "Archiving..."
                      : "Restoring..."
                    : pendingLifecycleAction.kind === "archive"
                      ? "Archive type"
                      : "Restore type"}
                </VNextButton>
              </>
            }
            onClose={closeLifecycleConfirmation}
            open
            title={
              pendingLifecycleAction.kind === "archive"
                ? "Archive business type"
                : "Restore business type"
            }
          >
            <div className="vnext-stack">
              <p>
                {pendingLifecycleAction.kind === "archive"
                  ? "This removes the business type from onboarding choices shown to operators and customers. Existing records are kept."
                  : "This adds the business type back to onboarding choices shown to operators and customers."}
              </p>
              <VNextKeyValueList
                items={[
                  {
                    label: "Business type",
                    value: pendingLifecycleAction.label,
                  },
                  {
                    label: "Effect",
                    value:
                      pendingLifecycleAction.kind === "archive"
                        ? "New onboarding sessions will not show this choice."
                        : "New onboarding sessions can show this choice again.",
                  },
                ]}
              />
            </div>
          </VNextDialog>
        ) : null}
      </div>
    </SettingsWorkspaceChrome>
  );
}
